import { db } from '../firebase/config'
import { doc, getDoc, increment, runTransaction } from 'firebase/firestore'
import { useUserStore } from '@/stores/userStore'
import { usePlansStore } from '@/stores/plansStore'

// Determine logical location bucket from a storage path
// Returns one of: 'user' | 'characters' | 'campaigns' | 'ai' | null
function getLocationFromPath(path) {
  if (!path || typeof path !== 'string') return null
  try {
    if (path.startsWith('users/')) return 'user'
    if (path.startsWith('characters/')) return 'characters'
    if (path.startsWith('campaigns/')) return 'campaigns'
    if (path.startsWith('ai-temp/') || path.startsWith('ai/')) return 'ai'
    // Treat rules editor assets as AI-generated content for location bucketing
    if (path.startsWith('rules-assets/')) return 'ai'
  } catch {
    /* ignore */
  }
  return null
}

/**
 * Service for tracking and enforcing storage quotas per user plan
 *
 * Storage usage is tracked in bytes in the user document: users/{uid}.storageUsed
 * Each plan defines a storageLimit in MB
 *
 * Usage:
 * - Before upload: await StorageQuotaService.checkQuota(userId, fileSize)
 * - After upload: await StorageQuotaService.trackUpload(userId, fileSize, metadata)
 * - After delete: await StorageQuotaService.trackDelete(userId, fileSize)
 */
class StorageQuotaService {
  /**
   * Get user's current storage usage and limit
   * @param {string} userId - User ID
   * @returns {Promise<{used: number, limit: number, available: number, usedMB: number, limitMB: number}>}
   */
  async getStorageInfo(userId) {
    if (!userId) {
      throw new Error('User ID is required')
    }

    try {
      // Get user document
      const userDoc = await getDoc(doc(db, 'users', userId))
      if (!userDoc.exists()) {
        throw new Error('User not found')
      }

      const userData = userDoc.data()
      const storageUsed = userData.storageUsed || 0 // bytes
      const planId = userData.plan || 'free'

      // Get plan limits
      const plansStore = usePlansStore()
      if (!plansStore.plans?.length) {
        await plansStore.fetchPlans()
      }

      const plan = plansStore.plans.find((p) => p.id === planId)
      const baseLimitMB = plan?.features?.storage?.maxStorageMB || 100 // Default 100MB

      // Check for active storage grants (storageGrants collection)
      let grantBonusMB = 0
      try {
        const { collection, query, where, getDocs } = await import('firebase/firestore')
        const grantsRef = collection(db, 'users', userId, 'storageGrants')
        const q = query(grantsRef, where('status', '==', 'active'))
        const grantsSnap = await getDocs(q)
        const now = Date.now()

        grantsSnap.forEach((grantDoc) => {
          const grant = grantDoc.data()
          // Check if grant is expired
          const expiresAt = grant.expiresAt?.toMillis?.() || grant.expiresAt
          if (expiresAt && expiresAt < now) return

          // Add storage bonus from grant (in MB)
          grantBonusMB += Number(grant.storageMB || 0)
        })
      } catch (err) {
        console.warn('[StorageQuotaService] Failed to fetch storage grants (non-fatal):', err)
      }

      const storageLimitMB = baseLimitMB + grantBonusMB
      const storageLimit = storageLimitMB * 1024 * 1024 // Convert MB to bytes

      return {
        used: storageUsed, // bytes
        limit: storageLimit, // bytes
        available: Math.max(0, storageLimit - storageUsed), // bytes
        usedMB: (storageUsed / (1024 * 1024)).toFixed(2), // MB for display
        limitMB: storageLimitMB, // MB for display
        baseLimitMB, // Base plan limit
        grantBonusMB, // Bonus from grants
        percentUsed: storageLimit > 0 ? ((storageUsed / storageLimit) * 100).toFixed(1) : 0
      }
    } catch (error) {
      console.error('[StorageQuotaService] Error getting storage info:', error)
      throw error
    }
  }

  /**
   * Check if user has enough storage quota for a file
   * @param {string} userId - User ID
   * @param {number} fileSize - File size in bytes
   * @returns {Promise<{allowed: boolean, reason?: string, storageInfo: Object}>}
   */
  async checkQuota(userId, fileSize) {
    if (!userId) {
      throw new Error('User ID is required')
    }

    if (!fileSize || fileSize <= 0) {
      throw new Error('Valid file size is required')
    }

    try {
      const userStore = useUserStore()
      if (userStore?.profile?.roles?.admin) {
        const storageInfo = await this.getStorageInfo(userId)
        return { allowed: true, storageInfo, adminBypass: true }
      }
      const storageInfo = await this.getStorageInfo(userId)

      // Check if file would exceed limit
      if (storageInfo.used + fileSize > storageInfo.limit) {
        const neededMB = (fileSize / (1024 * 1024)).toFixed(2)
        const availableMB = (storageInfo.available / (1024 * 1024)).toFixed(2)

        return {
          allowed: false,
          reason: `Not enough storage. Need ${neededMB}MB but only ${availableMB}MB available. Used ${storageInfo.usedMB}MB of ${storageInfo.limitMB}MB.`,
          storageInfo
        }
      }

      return {
        allowed: true,
        storageInfo
      }
    } catch (error) {
      console.error('[StorageQuotaService] Error checking quota:', error)
      throw error
    }
  }

  /**
   * Track a file upload and increment storage usage
   * Uses a transaction to ensure atomic updates
   * @param {string} userId - User ID
   * @param {number} fileSize - File size in bytes
   * @param {Object} metadata - Optional metadata about the upload
   * @returns {Promise<void>}
   */
  async trackUpload(userId, fileSize, metadata = {}) {
    if (!userId) {
      throw new Error('User ID is required')
    }

    if (!fileSize || fileSize <= 0) {
      throw new Error('Valid file size is required')
    }

    try {
      const userRef = doc(db, 'users', userId)

      await runTransaction(db, async (transaction) => {
        const userDoc = await transaction.get(userRef)

        if (!userDoc.exists()) {
          throw new Error('User not found')
        }

        const currentUsage = userDoc.data().storageUsed || 0
        const newUsage = currentUsage + fileSize

        const updates = {
          storageUsed: newUsage,
          lastStorageUpdate: new Date().toISOString(),
          // keep type-bytes aggregation
          ...(metadata.type && { [`storageByType.${metadata.type}`]: increment(fileSize) })
        }

        // maintain counts and by-location bytes if we know the path/location
        const loc = getLocationFromPath(metadata.path)
        if (loc) {
          updates[`storageByLocation.${loc}`] = increment(fileSize)
          updates[`fileCountsByLocation.${loc}`] = increment(1)
        }
        if (metadata.type) {
          updates[`fileCountsByType.${metadata.type}`] = increment(1)
        }
        updates['fileTotalCount'] = increment(1)

        // Update storage usage
        transaction.update(userRef, updates)
      })

      // console.log(`[StorageQuotaService] Tracked upload: ${fileSize} bytes for user ${userId}`)
    } catch (error) {
      console.error('[StorageQuotaService] Error tracking upload:', error)
      throw error
    }
  }

  /**
   * Track a file deletion and decrement storage usage
   * @param {string} userId - User ID
   * @param {number} fileSize - File size in bytes
   * @param {Object} metadata - Optional metadata about the deletion
   * @returns {Promise<void>}
   */
  async trackDelete(userId, fileSize, metadata = {}) {
    if (!userId) {
      throw new Error('User ID is required')
    }

    if (!fileSize || fileSize <= 0) {
      // Don't throw error for delete - just log warning
      console.warn('[StorageQuotaService] Invalid file size for deletion tracking:', fileSize)
      return
    }

    try {
      const userRef = doc(db, 'users', userId)

      await runTransaction(db, async (transaction) => {
        const userDoc = await transaction.get(userRef)

        if (!userDoc.exists()) {
          throw new Error('User not found')
        }

        const currentUsage = userDoc.data().storageUsed || 0
        const newUsage = Math.max(0, currentUsage - fileSize) // Never go negative

        const updates = {
          storageUsed: newUsage,
          lastStorageUpdate: new Date().toISOString(),
          // keep type-bytes aggregation
          ...(metadata.type && { [`storageByType.${metadata.type}`]: increment(-fileSize) })
        }

        const loc = getLocationFromPath(metadata.path)
        if (loc) {
          updates[`storageByLocation.${loc}`] = increment(-fileSize)
          updates[`fileCountsByLocation.${loc}`] = increment(-1)
        }
        if (metadata.type) {
          updates[`fileCountsByType.${metadata.type}`] = increment(-1)
        }
        updates['fileTotalCount'] = increment(-1)

        transaction.update(userRef, updates)
      })

      // console.log(`[StorageQuotaService] Tracked deletion: ${fileSize} bytes for user ${userId}`)
    } catch (error) {
      console.error('[StorageQuotaService] Error tracking delete:', error)
      // Don't throw - deletion should still succeed even if tracking fails
    }
  }

  /**
   * Recalculate user's storage usage by scanning all their files in Firebase Storage
   * This is useful for fixing discrepancies or initial setup
   * @param {string} userId - User ID
   * @returns {Promise<{totalBytes: number, fileCount: number, byType: Object}>}
   */
  async recalculateStorageUsage(userId) {
    if (!userId) {
      throw new Error('User ID is required')
    }

    // This would require implementing a cloud function to scan Firebase Storage
    // Since we can't easily list all files from client-side for security reasons
    // For now, this is a placeholder that admins could trigger server-side

    console.warn(
      '[StorageQuotaService] recalculateStorageUsage should be implemented as a cloud function'
    )

    return {
      totalBytes: 0,
      fileCount: 0,
      byType: {},
      note: 'This operation requires server-side implementation'
    }
  }

  /**
   * Format bytes to human-readable format
   * @param {number} bytes - Bytes
   * @param {number} decimals - Decimal places
   * @returns {string} Formatted string
   */
  formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return '0 Bytes'
    if (!bytes) return 'N/A'

    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']

    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
  }

  /**
   * Get composable helper for use in Vue components
   * @returns {Object} Helper functions
   */
  useStorageQuota() {
    const userStore = useUserStore()

    return {
      checkQuota: (fileSize) => this.checkQuota(userStore.userId, fileSize),
      trackUpload: (fileSize, metadata) => this.trackUpload(userStore.userId, fileSize, metadata),
      trackDelete: (fileSize, metadata) => this.trackDelete(userStore.userId, fileSize, metadata),
      getStorageInfo: () => this.getStorageInfo(userStore.userId),
      formatBytes: this.formatBytes
    }
  }
}

export default new StorageQuotaService()
